import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Button from '../components/ui/Button';
import { api } from '../services/api';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const TutorDeactivatePage = () => {
  useDocumentTitle('Remove from Active Tutor List');

  const { tutorId } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!tutorId) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md mx-auto bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <p className="text-red-400">Invalid link. Please check the email you received.</p>
        </div>
      </div>
    );
  }

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    const response = await api.tutors.deactivate(tutorId);
    if (response.success) {
      setSuccess(true);
    } else {
      setError(response.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md mx-auto bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-3">You've been removed from the active tutor list</h1>
          <p className="text-gray-400 text-sm">
            We've updated your status. We'll stop matching you with new students. If you change your mind,
            please contact us at{' '}
            <a href="mailto:aileen@mandarintutorhk.com" className="text-[#7e55f6] hover:underline">
              aileen@mandarintutorhk.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="max-w-md mx-auto bg-gray-900 border border-gray-800 rounded-xl p-8">
        <AlertTriangle size={48} className="text-yellow-400 mx-auto mb-4 block" />
        <h1 className="text-xl font-bold text-white mb-3 text-center">Remove Yourself from the Active Tutor List</h1>
        <p className="text-gray-400 text-sm mb-6 text-center">
          Confirming will mark you as inactive. We will stop matching you with new students.
          Your profile information will be retained but not shown to students.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex space-x-3">
          <Link to="/" className="flex-1">
            <Button
              variant="outline"
              className="w-full border-gray-600 text-gray-300"
              disabled={loading}
            >
              Cancel
            </Button>
          </Link>
          <Button
            variant="primary"
            onClick={handleConfirm}
            isLoading={loading}
            disabled={loading}
            className="flex-1 bg-red-600 hover:bg-red-700 border-red-600"
          >
            Yes, Remove Me
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TutorDeactivatePage;
